import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles, fade } from '@material-ui/core/styles';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  Typography,
} from '@material-ui/core';
import {
  CreateNewFolder as CreateNewFolderIcon,
  Edit as EditIcon,
} from '@material-ui/icons';
import { getFolderNameError } from './folderNameValidation';
import VisibilitaField from './VisibilitaField';
import {
  isVisibilitaAmmessa,
  isVisibilitaCompleta,
  stessaVisibilita,
} from './visibilitaAllegati';

const FOLDER_ICON_COLORS = { bg: '#FFF3E0', fg: '#F57C00' };

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: 16,
    overflow: 'hidden',
  },
  dialogTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: '20px 24px 12px',
  },
  dialogTitleIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  dialogTitleText: {
    fontWeight: 700,
    fontSize: '1.125rem',
    letterSpacing: '-0.01em',
  },
  dialogContent: {
    padding: '8px 24px 16px',
  },
  dialogForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
  },
  dialogActions: {
    padding: '12px 24px 20px',
    gap: theme.spacing(2),
  },
  dialogInput: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 10,
    },
  },
  dialogCancelBtn: {
    borderRadius: 10,
    textTransform: 'none',
    fontWeight: 500,
    padding: '8px 20px',
    color: theme.palette.text.secondary,
  },
  dialogSaveBtn: {
    borderRadius: 10,
    textTransform: 'none',
    fontWeight: 600,
    padding: '8px 24px',
    boxShadow: 'none',
    '&:hover': {
      boxShadow: `0 4px 12px ${fade(theme.palette.primary.main, 0.3)}`,
    },
  },
}));

/**
 * The folder form, for a new folder and for an existing one. Fully controlled:
 * the name and the visibility live in the caller's state, and the dialog only
 * says when the pair is worth saving — a non-empty valid name, a complete
 * visibility that the parent folder does not hide from, and at least one of
 * the two different from where it started.
 */
export default function FolderDialog({
  open,
  mode,
  titolo,
  titoloIniziale,
  visibilita,
  visibilitaIniziale,
  reparti,
  repartiAmmessi,
  saving,
  onClose,
  onTitoloChange,
  onVisibilitaChange,
  onSave,
}) {
  const classes = useStyles();
  const isCreate = mode === 'create';
  const titoloError = getFolderNameError(titolo);
  const trimmedTitolo = titolo.trim();
  const hasChanges =
    (isCreate && trimmedTitolo.length > 0) ||
    trimmedTitolo !== (titoloIniziale || '').trim() ||
    !stessaVisibilita(visibilita, visibilitaIniziale);
  const repartiEsistenti = reparti.stato === 'pronto' ? reparti.lista : null;
  const canSave =
    trimmedTitolo.length > 0 &&
    !titoloError &&
    isVisibilitaCompleta(visibilita) &&
    isVisibilitaAmmessa(visibilita, repartiAmmessi, repartiEsistenti) &&
    !saving &&
    hasChanges;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: classes.dialogPaper }}
    >
      <div className={classes.dialogTitle}>
        <div
          className={classes.dialogTitleIcon}
          style={{ backgroundColor: FOLDER_ICON_COLORS.bg }}
        >
          {isCreate ? (
            <CreateNewFolderIcon style={{ color: FOLDER_ICON_COLORS.fg }} />
          ) : (
            <EditIcon style={{ color: FOLDER_ICON_COLORS.fg }} />
          )}
        </div>
        <Typography className={classes.dialogTitleText}>
          {isCreate ? 'Nuova cartella' : 'Modifica cartella'}
        </Typography>
      </div>
      <DialogContent className={classes.dialogContent}>
        <div className={classes.dialogForm}>
          <TextField
            // With an `id` MUI ties the label to the field: without one the
            // label is text beside an input that has no name.
            id="cartella-nome"
            label="Nome cartella"
            variant="outlined"
            fullWidth
            autoFocus
            className={classes.dialogInput}
            value={titolo}
            onChange={(e) => onTitoloChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && canSave) onSave();
            }}
            error={!!titoloError}
            helperText={titoloError}
            placeholder="Es. Documenti HACCP"
          />
          <VisibilitaField
            id="cartella-visibilita"
            value={visibilita}
            onChange={onVisibilitaChange}
            reparti={reparti}
            repartiAmmessi={repartiAmmessi}
            disabled={saving}
          />
        </div>
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Button
          onClick={onClose}
          disabled={saving}
          className={classes.dialogCancelBtn}
        >
          Annulla
        </Button>
        <Button
          onClick={onSave}
          color="primary"
          variant="contained"
          disabled={!canSave}
          className={classes.dialogSaveBtn}
          style={{ color: '#fff' }}
        >
          {saving ? (
            <CircularProgress size={20} style={{ color: '#fff' }} />
          ) : isCreate ? (
            'Crea cartella'
          ) : (
            'Salva modifiche'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const repartoShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  nome: PropTypes.string.isRequired,
});

const visibilitaShape = PropTypes.shape({
  ristretta: PropTypes.bool.isRequired,
  reparti: PropTypes.arrayOf(repartoShape).isRequired,
});

FolderDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(['create', 'edit']).isRequired,
  titolo: PropTypes.string.isRequired,
  titoloIniziale: PropTypes.string,
  visibilita: visibilitaShape.isRequired,
  visibilitaIniziale: visibilitaShape.isRequired,
  reparti: PropTypes.shape({
    stato: PropTypes.oneOf(['caricamento', 'pronto', 'errore']).isRequired,
    lista: PropTypes.arrayOf(repartoShape).isRequired,
  }).isRequired,
  repartiAmmessi: PropTypes.arrayOf(repartoShape),
  saving: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onTitoloChange: PropTypes.func.isRequired,
  onVisibilitaChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

FolderDialog.defaultProps = {
  repartiAmmessi: null,
};
